import { isString } from 'lodash';

import { Postcode } from '@dreryk/address-common-api';
import { MedicalField } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { EmailAddress } from '@dreryk/email-address-common-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';

import { createInterpolatedTranslationKey, ValidateResult, ValidationRules } from '@dreryk/react-common';

import { serviceProvidingLocationTranslationKeys } from '../../../translations';

import {
   CITY_VALIDATION_RULES,
   FLAT_VALIDATION_RULES,
   HOUSE_VALIDATION_RULES,
   PHONE_NUMBER_VALIDATION_RULES,
   POSTCODE_VALIDATION_RULES,
   STREET_VALIDATION_RULES,
} from './address';

export type ServiceProvidingLocationFormBean = {
   id?: number | null;
   city: string | null;
   emailAddress: EmailAddress | null;
   flat: string | null;
   house: string | null;
   locationCode: string;
   serviceProvidingLocationName: string | null;
   phoneNumber: PhoneNumber | null;
   postcode: Postcode | null;
   street: string | null;
   usingHealthcarePracticeDetails: boolean | null;
   medicalField: MedicalField | null;
};

const SERVICE_PROVIDING_LOCATION_NAME_MAX_LENGTH = 255;

export const SERVICE_PROVIDING_LOCATION_NAME_VALIDATION_RULES: ValidationRules<{
   serviceProvidingLocationName: string | null;
}> = {
   required: true,
   validate: (data: unknown): ValidateResult => {
      if (isString(data) && data.length <= SERVICE_PROVIDING_LOCATION_NAME_MAX_LENGTH) {
         return undefined;
      }

      return createInterpolatedTranslationKey(serviceProvidingLocationTranslationKeys.invalidNameLength, {
         maxLength: SERVICE_PROVIDING_LOCATION_NAME_MAX_LENGTH,
      });
   },
};

const validateRules = (data: unknown, validationRules: ValidationRules<ServiceProvidingLocationFormBean>): boolean => {
   if (validationRules.required && !data) {
      return true;
   }

   if (isString(data)) {
      if (validationRules.minLength && data.length < Number(validationRules.minLength)) {
         return true;
      }

      if (validationRules.maxLength && data.length > Number(validationRules.maxLength)) {
         return true;
      }
   }

   return false;
};

export const isServiceProvidingLocationBeanInvalid = (bean: ServiceProvidingLocationFormBean): boolean =>
   validateRules(bean.serviceProvidingLocationName, SERVICE_PROVIDING_LOCATION_NAME_VALIDATION_RULES) ||
   validateRules(bean.city, CITY_VALIDATION_RULES) ||
   validateRules(bean.street, STREET_VALIDATION_RULES) ||
   validateRules(bean.house, HOUSE_VALIDATION_RULES) ||
   validateRules(bean.flat, FLAT_VALIDATION_RULES) ||
   validateRules(bean.phoneNumber, PHONE_NUMBER_VALIDATION_RULES) ||
   validateRules(bean.postcode, POSTCODE_VALIDATION_RULES);
